'use client'

// The ONE place the checkout asks the backend to price the cart before an order
// exists. PaymentPage calls this once and hands the result to BOTH of its summary
// slots (the mobile one between the address and payment steps, the desktop one in
// the side column) — the previous design mounted a self-fetching component in each
// slot, so every cart change fired the backend call TWICE.
//
// What it costs the backend: previewInvoiceWithoutOrder is a pure READ — it builds
// a transient Order in memory, stamps sale prices on transient item copies, and
// never opens a transaction or persists anything (OrdersModel.getItemsForInvoicePreview,
// ChargedOrderInfo). Measured on production 2026-09-09/10 after the 08-29→09-01
// pricing-path fixes: p50 0.55s, p90 0.85s, max 1.8s over 479 calls, zero 5xx
// (2026-08-23, before those fixes: p50 4.5s, p90 10.8s, max 24.6s, 23 500s).
//
// When it fires: once on mount, then once per DISCRETE customer action — a
// discount-code Apply, a +/- in the cart panel, a shipping-country pick. Never
// per keystroke: the postal code is deliberately NOT a dependency (and is not
// sent — shipping is excluded from the preview, see the comment at the call).
// Follow-up changes are debounced so a run of +/- clicks costs one call, and a
// response that arrives after a newer request was issued is dropped, so the
// summary can never show a stale cart's total.
import { useCallback, useEffect, useRef, useState } from 'react'
import { http } from '../../../../../utils/clientHttp'
import { useRegionCountryCode } from '../../../../RegionGeo'
import { useCartStore, selectCart } from '../../../../../store/cartStore'
import { useSessionStore } from '../../../../../store/sessionStore'
import { classifyPreviewFailure } from './previewFailure'

/** Coalesce a burst of cart edits into one backend call. */
export const PREVIEW_DEBOUNCE_MS = 300

/**
 * @typedef {Object} InvoicePreviewQuote
 * @property {any|null} invoice           the priced invoice (InvoiceDto), or null
 * @property {boolean} isLoading          a request is pending (or debouncing)
 * @property {import('./previewFailure').PreviewFailure|null} failure
 * @property {string|null} discountWarning the code was NOT applied, and why (from a 200)
 * @property {() => void} retry           re-issue the same request now
 */

/**
 * @param {{ discountCode: string|null, enabled?: boolean }} args
 *   discountCode — the code to price with (the customer's submitted code, else the
 *                  session's); a change re-quotes.
 *   enabled      — false while there is nothing to price (empty cart) or an order
 *                  already exists (its invoice replaces the preview).
 * @returns {InvoicePreviewQuote}
 */
export function useInvoicePreviewQuote({ discountCode, enabled = true }) {
  const shoppingCart = useCartStore(selectCart)
  const newRegion = useRegionCountryCode()
  const affiliateCode = useSessionStore((s) => s.affiliateCode)

  const [state, setState] = useState({
    invoice: null,
    isLoading: true,
    failure: null,
    discountWarning: null,
  })
  const [attempt, setAttempt] = useState(0)
  // Monotonic request id: a response is applied only if it belongs to the LATEST
  // request, so a slow earlier quote cannot overwrite a newer one.
  const requestSeq = useRef(0)

  useEffect(() => {
    // The region store can still be resolving when checkout first mounts.
    // Wait for its country instead of sending a null country to tax pricing.
    if (!enabled || !newRegion) return undefined

    const seq = ++requestSeq.current
    setState((s) => ({ ...s, isLoading: true, failure: null }))

    const fire = () => {
      http
        .post('/api/payments/previewInvoiceWithoutOrder', {
          discountCode: discountCode || null,
          // Shipping is excluded from the preview (the backend prices with
          // excludeShipping when postalCode is null): sending the postal code
          // produced an incorrect shipping line, and typing one would otherwise
          // re-quote per keystroke. The order's own invoice replaces this preview
          // as soon as the address is submitted.
          postalCode: null,
          country: newRegion,
          itemsAndCount: shoppingCart,
          affiliateCode: affiliateCode || null,
        })
        .then((response) => {
          if (seq !== requestSeq.current) return
          const data = response && response.data ? response.data : null
          const discountWarning = data && data.discountWarning ? data.discountWarning : null
          if (discountWarning) {
            // The code was invalid/expired and was NOT applied. Stop persisting it
            // so it isn't silently re-sent on every visit (the bug where a bad saved
            // code made checkout impossible until storage was cleared).
            useSessionStore.getState().resetDiscountCode()
          } else {
            // Save the code to the session ONLY once the preview applied it.
            useSessionStore.getState().setDiscountCode(discountCode || null)
          }
          setState({ invoice: data, isLoading: false, failure: null, discountWarning })
        })
        .catch((e) => {
          if (seq !== requestSeq.current) return
          // `e.response` is UNDEFINED for a network error / abort; the classifier
          // reads it guarded (the 1dbe5854 fix), and this branch ALWAYS settles
          // isLoading so the spinner can never span forever.
          console.log('[invoice-preview] quote failed', e && (e.code || e.message))
          setState({ invoice: null, isLoading: false, failure: classifyPreviewFailure(e), discountWarning: null })
        })
    }

    // The first quote goes out immediately (it is what the customer is waiting
    // for); follow-up changes are coalesced.
    if (seq === 1) {
      fire()
      return undefined
    }
    const timer = setTimeout(fire, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [enabled, discountCode, newRegion, shoppingCart, affiliateCode, attempt])

  const retry = useCallback(() => setAttempt((a) => a + 1), [])

  return { ...state, retry }
}

export default useInvoicePreviewQuote
